import * as React from "react"
import { format } from "date-fns"
import { CalendarBlank, X } from "@phosphor-icons/react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

interface DatePickerProps {
  /** Selected date */
  value?: Date
  /** Callback when date changes */
  onChange?: (date: Date | undefined) => void
  /** Placeholder text when no date selected */
  placeholder?: string
  /** Whether the picker is disabled */
  disabled?: boolean
  /** Additional CSS classes for the trigger button */
  className?: string
  /** Format string for displaying the date */
  displayFormat?: string
  /** Show a clear button when a value is set */
  clearable?: boolean
}

/**
 * DatePicker - A date picker with calendar popover
 *
 * Uses react-day-picker for the calendar and Radix popover for the dropdown.
 */
/** Parse a date string (YYYY-MM-DD or full ISO) to a local Date, avoiding timezone shift */
export function parseLocalDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined
  // Always extract just the date part to avoid UTC midnight shifting to the previous day
  const datePart = value.split("T")[0]
  const date = new Date(datePart + "T00:00:00")
  return isNaN(date.getTime()) ? undefined : date
}

/** Format a Date to YYYY-MM-DD string for form/API storage */
export function formatLocalDate(date: Date | undefined): string {
  if (!date) return ""
  return format(date, "yyyy-MM-dd")
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
  displayFormat = "PPP",
  clearable = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    onChange?.(undefined)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarBlank className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {value ? format(value, displayFormat) : placeholder}
          </span>
          {clearable && value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              className="ml-auto pl-1 opacity-50 hover:opacity-100 transition-opacity"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleClear(e)
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
